import {
  Alert,
  Box,
  LoadingSkeleton,
  DataTable,
  FilterSelect,
  Grid,
  HairlineCard,
  Icons,
  Stack,
  StatusChip,
  Typography,
} from '@munifocus/ui';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import {
  COST_RECOVERY_STATUSES,
  type CostLedgerRow,
  type CostRecoveryStatus,
} from '@munifocus/contracts-gov';
import { AppShell } from '../components/AppShell';
import { ModuleOffNotice } from '../components/ModuleOffNotice';
import { PageIntro } from '../components/PageIntro';
import { useOptionalScope } from '../providers/ScopeProvider';
import { useCostLedger, useCostSettings } from '../lib/cost-recovery-api';
import { day, money, RECOVERY_STATUS_KIND } from '../lib/cost-recovery-display';
import { apiErrorStatus } from '../lib/mutation-toast';

function Kpi({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <HairlineCard sx={{ p: 2, height: '100%' }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h5" component="p" sx={{ fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </Typography>
      {hint && (
        <Typography variant="caption" color="text.secondary">
          {hint}
        </Typography>
      )}
    </HairlineCard>
  );
}

/**
 * The municipality's book of recoverable costs: every file with costs on it,
 * what was billed, what came back, what is still owed and how late it is.
 * Most overdue first — the order a finance officer works the list in.
 */
export function CostLedger() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const scope = useOptionalScope();
  const lg = scope?.active?.kind === 'local_government' ? scope.active : null;
  // The settings never 404, so they decide whether the ledger is worth asking
  // for: with the module off it could only answer 404.
  const settings = useCostSettings(lg?.id, !!scope?.can('cost:read'));
  const moduleOff = settings.data?.enabled === false;
  const ledger = useCostLedger(settings.data?.enabled ? lg?.id : undefined);
  const [status, setStatus] = useState<CostRecoveryStatus | 'all'>('all');

  const rows = useMemo(
    () => (ledger.data?.rows ?? []).filter((r) => status === 'all' || r.status === status),
    [ledger.data, status],
  );
  const totals = ledger.data?.totals;

  return (
    <AppShell>
      <PageIntro>{t('gov.costRecovery.ledger.intro', { place: lg?.name ?? '' })}</PageIntro>
      <Stack spacing={2}>
        {!lg && <Alert severity="info">{t('gov.costRecovery.ledger.pickMunicipality')}</Alert>}
        {(ledger.isLoading || settings.isLoading) && (
          <LoadingSkeleton variant="ledger" count={6} label={t('common.labels.loading')} />
        )}
        {/* Off here: say what the module is and who turns it on, rather than
            one line above a screen that will never fill. The settings read
            says so up front; a 404 from the ledger is the same answer late. */}
        {(moduleOff || (ledger.isError && apiErrorStatus(ledger.error) === 404)) && (
          <ModuleOffNotice
            icon={Icons.Banknote}
            title={t('gov.costRecovery.notEnabled')}
            what={t('gov.costRecovery.moduleOff.what')}
            who={t('gov.costRecovery.moduleOff.who')}
          />
        )}
        {ledger.isError && apiErrorStatus(ledger.error) !== 404 && (
          <Alert severity="error">{t('gov.costRecovery.ledger.loadError')}</Alert>
        )}

        {totals && (
          <Grid container spacing={2}>
            <Grid size={{ xs: 6, md: 3 }}>
              <Kpi
                label={t('gov.costRecovery.ledger.outstanding')}
                value={money(totals.outstandingCents, lang)}
                hint={t('gov.costRecovery.ledger.interestIncluded', {
                  amount: money(totals.interestCents, lang),
                })}
              />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Kpi
                label={t('gov.costRecovery.ledger.recovered')}
                value={money(totals.recoveredCents, lang)}
                hint={t('gov.costRecovery.ledger.ofBilled', {
                  amount: money(totals.billedCents, lang),
                })}
              />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Kpi
                label={t('gov.costRecovery.ledger.overdue')}
                value={money(totals.overdueCents, lang)}
                hint={t('gov.costRecovery.ledger.overdueFiles', {
                  count: totals.byStatus.overdue + totals.byStatus.escalated,
                })}
              />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Kpi
                label={t('gov.costRecovery.ledger.notBilled')}
                value={money(totals.pendingCents + totals.unbilledCents, lang)}
                hint={t('gov.costRecovery.ledger.notBilledHint', {
                  pending: money(totals.pendingCents, lang),
                })}
              />
            </Grid>
          </Grid>
        )}

        {ledger.data && (
          <DataTable
            rows={rows}
            getRowKey={(row) => row.fileId}
            label={t('gov.costRecovery.ledger.title')}
            // Six columns fit a 1240px laptop with the rail open (about 930px
            // of card). 980 forced a scrollbar there and cut "Due" in half;
            // below this, on a tablet or phone, the table scrolls instead.
            minWidth={760}
            pageSize={25}
            emptyLabel={t('gov.costRecovery.ledger.empty')}
            rowHref={(row) => `/files/${row.fileId}/cost-recovery`}
            linkComponent={RouterLink}
            search={{
              placeholder: t('gov.costRecovery.ledger.search'),
              of: (row) => `${row.reference} ${row.address} ${row.ownerName ?? ''}`,
            }}
            filters={
              <FilterSelect
                label={t('gov.costRecovery.ledger.statusFilter')}
                value={status}
                onChange={(v) => setStatus(v as CostRecoveryStatus | 'all')}
                options={COST_RECOVERY_STATUSES.map((s) => ({
                  value: s,
                  label: t(`gov.costRecovery.status.${s}`),
                }))}
              />
            }
            columns={[
              {
                key: 'reference',
                label: t('gov.costRecovery.ledger.col.file'),
                primary: true,
                mono: true,
                nowrap: true,
                sortValue: (row: CostLedgerRow) => row.reference,
                cell: (row: CostLedgerRow) => row.reference,
              },
              {
                key: 'address',
                label: t('gov.costRecovery.ledger.col.property'),
                sortValue: (row: CostLedgerRow) => row.address,
                cell: (row: CostLedgerRow) => (
                  <Stack spacing={0.25}>
                    <Typography variant="body2">{row.address}</Typography>
                    {row.ownerName && (
                      <Typography variant="caption" color="text.secondary">
                        {row.ownerName}
                      </Typography>
                    )}
                  </Stack>
                ),
              },
              {
                key: 'status',
                label: t('gov.costRecovery.ledger.col.status'),
                sortValue: (row: CostLedgerRow) => row.status,
                cell: (row: CostLedgerRow) => (
                  <StatusChip
                    status={RECOVERY_STATUS_KIND[row.status]}
                    label={
                      row.escalation
                        ? t(`gov.costRecovery.escalation.${row.escalation}`)
                        : t(`gov.costRecovery.status.${row.status}`)
                    }
                  />
                ),
              },
              {
                key: 'billed',
                label: t('gov.costRecovery.ledger.col.billed'),
                align: 'right',
                nowrap: true,
                optional: true,
                sortValue: (row: CostLedgerRow) => row.billedCents,
                cell: (row: CostLedgerRow) => money(row.billedCents, lang),
              },
              {
                key: 'paid',
                label: t('gov.costRecovery.ledger.col.paid'),
                align: 'right',
                nowrap: true,
                sortValue: (row: CostLedgerRow) => row.paidCents,
                cell: (row: CostLedgerRow) => money(row.paidCents, lang),
              },
              {
                key: 'balance',
                label: t('gov.costRecovery.ledger.col.balance'),
                align: 'right',
                nowrap: true,
                sortValue: (row: CostLedgerRow) => row.balanceCents,
                cell: (row: CostLedgerRow) => money(row.balanceCents, lang),
              },
              {
                key: 'dueOn',
                label: t('gov.costRecovery.ledger.col.due'),
                nowrap: true,
                sortValue: (row: CostLedgerRow) => row.dueOn ?? '',
                cell: (row: CostLedgerRow) =>
                  row.daysOverdue > 0
                    ? t('gov.costRecovery.ledger.daysLate', {
                        date: day(row.dueOn, lang),
                        count: row.daysOverdue,
                      })
                    : day(row.dueOn, lang),
              },
            ]}
          />
        )}
      </Stack>
    </AppShell>
  );
}
